// RITA-1585 — Reine Platzregeln für die Handbuch-Leiste, ohne DOM-Zugriff (Skizze 31.10.2026, Schwellentabelle).
// Die Leiste steht als Spalte rechts neben dem Inhalt, solange das ganze Blatt daneben Platz hat; sonst legt sie
// sich als Schublade über den rechten Rand. Die Vertragsseite wird nie verkleinert (Entscheidung Julia 31.10.2026).
// Gemessen wird die Zeile innerhalb des Seitenrahmens, nicht die Fensterbreite – wie bei der Platzhalterleiste.

export type Handbuchdarstellung = 'spalte' | 'schublade';

export const HANDBUCH_BREITE_PX = 380;
/** Lücke zwischen Inhalt und Handbuch-Spalte (`gap: 2`). */
export const HANDBUCH_LUECKE_PX = 16;
/**
 * Spielraum für den Weg zurück von der Schublade zur Spalte: Mit der Darstellung kann eine Bildlaufleiste
 * erscheinen oder verschwinden; ohne Spielraum spränge die Leiste an der Grenze hin und her.
 */
export const HANDBUCH_HYSTERESE_PX = 24;

/** Zeilenbreite, ab der die Spalte neben einem Inhalt dieser Mindestbreite Platz hat. */
export function handbuchBedarfPx(mindestInhaltBreitePx: number): number {
  return mindestInhaltBreitePx + HANDBUCH_LUECKE_PX + HANDBUCH_BREITE_PX;
}

/**
 * Wirksame Darstellung aus gemessener Zeilenbreite und Mindestbreite des Inhalts (Blatt samt Seitenrahmen).
 * Der Spielraum gilt nur für den Rückweg aus der Schublade (`bisherSchublade`). Ohne messbare Breite (null/0:
 * vor der ersten Messung, jsdom) gilt die Spalte.
 */
export function wirksameHandbuchdarstellung(
  zeilenBreitePx: number | null,
  mindestInhaltBreitePx: number,
  bisherSchublade: boolean,
): Handbuchdarstellung {
  if (!zeilenBreitePx || zeilenBreitePx <= 0) return 'spalte';
  const bedarf = handbuchBedarfPx(mindestInhaltBreitePx);
  const grenze = bisherSchublade ? bedarf + HANDBUCH_HYSTERESE_PX : bedarf;
  return zeilenBreitePx >= grenze ? 'spalte' : 'schublade';
}

/**
 * Wie viel nebeneinander passt: 0 = Schublade, 1 = Spalte (Platzhalterleiste oben), 2 = Spalte und
 * Platzhalterleiste seitlich. `leistenBedarfPx` ist die Rahmenbreite, die die seitliche Platzhalterleiste braucht;
 * null auf Seiten ohne Platzhalterleiste (Vertrag am Berater).
 */
export type Platzstufe = 0 | 1 | 2;

/** `spielraumPx` hebt beide Schwellen an – für den Blick auf einen Zustand, der erst noch erreicht werden muss. */
export function platzstufe(
  zeilenBreitePx: number,
  mindestInhaltBreitePx: number,
  leistenBedarfPx: number | null,
  spielraumPx = 0,
): Platzstufe {
  if (zeilenBreitePx < handbuchBedarfPx(mindestInhaltBreitePx) + spielraumPx) return 0;
  const inhaltBreite = zeilenBreitePx - HANDBUCH_LUECKE_PX - HANDBUCH_BREITE_PX;
  if (leistenBedarfPx !== null && inhaltBreite >= leistenBedarfPx + spielraumPx) return 2;
  return 1;
}

/**
 * Das Menü links wird nur dann schmal, wenn dadurch mehr nebeneinander passt (Entscheidung Julia 31.10.2026,
 * Skizze Bild 2): `gewinnPx` ist die Breite, die das schmale Menü freigibt (280 − 88 = 192). Der Zustand nach der
 * Umschaltung wird mit dem Spielraum der Darstellung gerechnet: Handbuch und Platzhalterleiste kehren erst mit
 * Spielraum zur Spalte bzw. Seite zurück – ohne ihn würde das Menü in einem 24-px-Band schmal, ohne dass sich
 * daneben etwas ändert.
 */
export function menueSchmalLohntSich(
  zeilenBreitePx: number | null,
  gewinnPx: number,
  mindestInhaltBreitePx: number,
  leistenBedarfPx: number | null,
  spielraumPx: number = HANDBUCH_HYSTERESE_PX,
): boolean {
  if (!zeilenBreitePx || zeilenBreitePx <= 0 || gewinnPx <= 0) return false;
  return (
    platzstufe(zeilenBreitePx + gewinnPx, mindestInhaltBreitePx, leistenBedarfPx, spielraumPx) >
    platzstufe(zeilenBreitePx, mindestInhaltBreitePx, leistenBedarfPx)
  );
}
